// Affiliate partner portal — /affiliate/login + /affiliate/dashboard.
//
// Partner-facing and completely separate from staff auth and the client
// portal. Uses plain fetch with its own token (localStorage 'nx.aff.token') so
// the staff API wrapper's global 401 redirect never touches these pages.
//
// What a partner sees is DELIBERATELY narrow: her branded link, the clients
// she brought in and where they are in the journey, what got approved, her
// commissions, and her payout statements. Never the credit analysis, scores,
// negatives, lender matching, or how we qualify.
import { useEffect, useMemo, useState } from "react";
import {
  Copy, LogOut, MousePointerClick, DollarSign, Users, CheckCircle2, Wallet,
  TrendingUp, Link2, Receipt, BadgeCheck, Clock, Banknote, KeyRound, ImagePlus, Trash2, FileCheck2,
} from "lucide-react";
import { partnerTerms } from "../lib/partner-terms";

const TOKEN_KEY = "nx.aff.token";
const money = (n: number) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (n: number) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

async function api(path: string, opts: { method?: string; body?: any } = {}) {
  const res = await fetch(`/api/affiliate-portal/${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(localStorage.getItem(TOKEN_KEY) ? { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  return data;
}

async function apiUpload(path: string, file: File) {
  const fd = new FormData(); fd.append("file", file);
  const res = await fetch(`/api/affiliate-portal/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  return data;
}

/** Optional business logo — shown on her branded funnel and her dashboard. Add now or any time later. */
function LogoUploader({ logoUrl, businessName, onChange, compact }: { logoUrl: string | null; businessName: string; onChange: () => void; compact?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function pick(file: File | null | undefined) {
    if (!file) return;
    setBusy(true); setErr(null);
    try { await apiUpload("logo", file); onChange(); }
    catch (e: any) { setErr(e?.message || "Could not upload that."); }
    finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true); setErr(null);
    try {
      const res = await fetch("/api/affiliate-portal/logo", { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });
      if (!res.ok) throw new Error("Could not remove the logo.");
      onChange();
    } catch (e: any) { setErr(e?.message); } finally { setBusy(false); }
  }
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={{ display: "grid", placeItems: "center", width: compact ? 56 : 72, height: compact ? 56 : 72, borderRadius: 12, background: "#fff", border: "1px solid var(--ne-line)", overflow: "hidden", flex: "0 0 auto" }}>
          {logoUrl
            ? <img src={logoUrl} alt={`${businessName} logo`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            : <ImagePlus size={22} aria-hidden="true" style={{ color: "var(--ne-black-3, #888)" }} />}
        </span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label className="ghost-button" style={{ cursor: "pointer" }}>
            <ImagePlus size={14} aria-hidden="true" /> {busy ? "Uploading…" : logoUrl ? "Replace logo" : "Add your logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={busy}
              onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {logoUrl && <button className="ghost-button" disabled={busy} onClick={remove}><Trash2 size={14} aria-hidden="true" /> Remove</button>}
        </div>
      </div>
      <p className="muted small" style={{ margin: "8px 0 0" }}>Optional. PNG, JPG, or WebP up to 1 MB. Shows on your referral page next to the company's name.{compact ? "" : " You can add or change it any time from your Account tab."}</p>
      {err && <p className="error-text" style={{ marginTop: 6 }}>{err}</p>}
    </div>
  );
}

/** The agreement she ticks. Scrollable so the whole thing is on the page, not behind a link. */
function TermsBox({ company, pct }: { company: string; pct: number }) {
  const t = partnerTerms(company, pct);
  return (
    <div style={{ border: "1px solid var(--ne-line)", borderRadius: 10, background: "var(--ne-black-2)", padding: "12px 14px", maxHeight: 220, overflowY: "auto" }}>
      <strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>{t.title}</strong>
      <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 8 }}>
        {t.points.map((p, i) => <li key={i} className="muted small" style={{ lineHeight: 1.5 }}>{p}</li>)}
      </ol>
    </div>
  );
}

/* ─────────────────────────── login ─────────────────────────── */

export function AffiliateLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (localStorage.getItem(TOKEN_KEY)) window.location.href = "/affiliate/dashboard"; }, []);

  async function login(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api("login", { method: "POST", body: { email, password } });
      localStorage.setItem(TOKEN_KEY, r.token);
      window.location.href = "/affiliate/dashboard";
    } catch (e: any) { setErr(e?.message || "Login failed"); setBusy(false); }
  }

  return (
    <div className="landing" style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
      <form onSubmit={login} className="landing-card" style={{ width: "min(420px, calc(100vw - 40px))", padding: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 10, background: "var(--ne-red-bright)", color: "#fff" }}>
            <BadgeCheck size={22} aria-hidden="true" />
          </span>
          <div>
            <p className="landing-kicker" style={{ margin: 0 }}>Partner portal</p>
            <h1 style={{ fontSize: 22, margin: 0 }}>Sign in</h1>
          </div>
        </div>
        <p className="muted small" style={{ marginBottom: 18 }}>Use the login from your welcome email. Lost it? Ask the company that added you.</p>
        <label className="form-field" style={{ marginBottom: 12 }}><span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="username" /></label>
        <label className="form-field" style={{ marginBottom: 16 }}><span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></label>
        {err && <p className="error-text" style={{ marginBottom: 10 }}>{err}</p>}
        <button className="primary-button" style={{ width: "100%", justifyContent: "center" }} disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/* ─────────────────────────── dashboard ─────────────────────────── */

const STAGE: Record<string, { label: string; tone: string }> = {
  pending:    { label: "Invited",        tone: "" },
  onboarding: { label: "In progress",    tone: "pill-amber" },
  active:     { label: "Active",         tone: "pill-green" },
  funded:     { label: "Funded",         tone: "pill-green" },
  closed:     { label: "Closed",         tone: "" },
};

function Stat({ icon: Icon, label, value, sub, accent }: { icon: any; label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="landing-card" style={{ padding: 18, display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 10, flex: "0 0 auto",
        background: accent ? "var(--ne-red-bright)" : "var(--ne-black-2)", color: accent ? "#fff" : "var(--ne-red-bright)", border: accent ? "none" : "1px solid var(--ne-line)" }}>
        <Icon size={20} aria-hidden="true" />
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="muted small" style={{ letterSpacing: ".06em", textTransform: "uppercase", fontSize: 11 }}>{label}</div>
        <div style={{ fontFamily: "'Bebas Neue', Inter, sans-serif", fontSize: 30, lineHeight: 1.05, marginTop: 2 }}>{value}</div>
        {sub && <div className="muted small" style={{ marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export function AffiliateDashboardPage() {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<"clients" | "commissions" | "payouts" | "account">("clients");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [ob, setOb] = useState({ name: "", business_name: "", phone: "", agree: false });
  const [agreeLater, setAgreeLater] = useState(false);
  const [agreeBusy, setAgreeBusy] = useState(false);
  const reload = () => api("me").then(setData).catch(() => {});
  const [obBusy, setObBusy] = useState(false);
  const [obErr, setObErr] = useState<string | null>(null);
  const [pwMsg, setPwMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { window.location.href = "/affiliate/login"; return; }
    api("me").then(setData).catch((e) => {
      if (/401|unauthorized/i.test(String(e.message))) { localStorage.removeItem(TOKEN_KEY); window.location.href = "/affiliate/login"; }
      else setErr(e?.message || "Could not load your dashboard.");
    });
  }, []);

  function logout() { localStorage.removeItem(TOKEN_KEY); window.location.href = "/affiliate/login"; }
  function copyLink() { navigator.clipboard.writeText(data.affiliate.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }

  const a = data?.affiliate; const t = data?.totals || {};
  const clients: any[] = data?.clients || [];
  const referrals: any[] = data?.referrals || [];
  const payouts: any[] = data?.payouts || [];
  const displayName = a?.business_name || a?.name;

  const funnel = useMemo(() => ({
    invited: clients.length,
    applied: clients.filter((c) => c.intake_done).length,
    approved: clients.filter((c) => Number(c.approvals) > 0).length,
  }), [clients]);

  if (err) return <div className="landing" style={{ padding: 40 }}><p className="error-text">{err}</p></div>;
  if (!data) return <div className="landing" style={{ padding: 40 }}><p className="muted">Loading…</p></div>;

  // ── first login: she tells us who she is; this mints her branded link ──
  if (data.affiliate && !data.affiliate.onboarding_complete) {
    const preview = ob.business_name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    return (
      <div className="landing" style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <div className="landing-card" style={{ width: "min(520px, calc(100vw - 40px))", padding: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 10, background: "var(--ne-red-bright)", color: "#fff" }}>
              <BadgeCheck size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="landing-kicker" style={{ margin: 0 }}>Welcome to the partner program</p>
              <h1 style={{ fontSize: 22, margin: 0 }}>Set up your profile</h1>
            </div>
          </div>
          <p className="muted small" style={{ marginBottom: 18 }}>
            {data.affiliate.company_name} set you up to earn <strong style={{ color: "var(--ne-text)" }}>{data.affiliate.commission_pct}% of the success fee</strong> on
            every client you refer who gets funded. Your name and business name create your referral link.
          </p>
          <label className="form-field" style={{ marginBottom: 12 }}><span>Your name</span>
            <input value={ob.name} onChange={(e) => setOb({ ...ob, name: e.target.value })} placeholder="First and last name" autoComplete="name" /></label>
          <label className="form-field" style={{ marginBottom: 12 }}><span>Business name</span>
            <input value={ob.business_name} onChange={(e) => setOb({ ...ob, business_name: e.target.value })} placeholder="What your clients know you as" /></label>
          <label className="form-field" style={{ marginBottom: 6 }}><span>Phone (optional)</span>
            <input value={ob.phone} onChange={(e) => setOb({ ...ob, phone: e.target.value })} inputMode="tel" /></label>
          {preview && (
            <p className="muted small" style={{ margin: "10px 0 0" }}>
              Your referral link will be <code>alieffundinghub.com/with/{preview}</code>
            </p>
          )}
          <div style={{ margin: "18px 0 6px" }}>
            <span className="muted small" style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>Business logo (optional)</span>
            <LogoUploader logoUrl={data.affiliate.logo_url} businessName={ob.business_name || "Your"} onChange={reload} />
          </div>
          <div style={{ marginTop: 18 }}>
            <span className="muted small" style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>Partner agreement</span>
            <TermsBox company={data.affiliate.company_name} pct={data.affiliate.commission_pct} />
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={ob.agree} onChange={(e) => setOb({ ...ob, agree: e.target.checked })} style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--ne-red-bright)" }} />
              <span className="small">I've read the agreement and I accept it — including that my commission is {data.affiliate.commission_pct}% of the success fee {data.affiliate.company_name} collects on each funded client I refer.</span>
            </label>
          </div>
          {obErr && <p className="error-text" style={{ marginTop: 10 }}>{obErr}</p>}
          <button className="primary-button" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}
            disabled={obBusy || ob.name.trim().split(/\s+/).length < 2 || !ob.business_name.trim() || !ob.agree}
            onClick={async () => {
              setObBusy(true); setObErr(null);
              try {
                await api("onboard", { method: "POST", body: { name: ob.name.trim(), business_name: ob.business_name.trim(), phone: ob.phone.trim() || undefined, agree: true } });
                setData(await api("me"));
              } catch (e: any) { setObErr(e?.message || "Could not save that."); }
              finally { setObBusy(false); }
            }}>
            {obBusy ? "Creating your link…" : "Accept & create my referral link"}
          </button>
        </div>
      </div>
    );
  }

  // ── already onboarded (or terms bumped) but no acceptance on file: agree before the dashboard ──
  if (data.affiliate && (!data.affiliate.agreement_accepted_at || !data.affiliate.agreement_current)) {
    return (
      <div className="landing" style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <div className="landing-card" style={{ width: "min(560px, calc(100vw - 40px))", padding: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 10, background: "var(--ne-red-bright)", color: "#fff" }}>
              <FileCheck2 size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="landing-kicker" style={{ margin: 0 }}>{data.affiliate.agreement_accepted_at ? "Updated terms" : "One more thing"}</p>
              <h1 style={{ fontSize: 22, margin: 0 }}>Partner agreement</h1>
            </div>
          </div>
          <p className="muted small" style={{ marginBottom: 14 }}>
            Please read and accept the partner agreement to open your dashboard. Your commission is{" "}
            <strong style={{ color: "var(--ne-text)" }}>{data.affiliate.commission_pct}% of the success fee</strong> {data.affiliate.company_name} collects on each funded client you refer.
          </p>
          <TermsBox company={data.affiliate.company_name} pct={data.affiliate.commission_pct} />
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={agreeLater} onChange={(e) => setAgreeLater(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--ne-red-bright)" }} />
            <span className="small">I've read the agreement and I accept it.</span>
          </label>
          {obErr && <p className="error-text" style={{ marginTop: 10 }}>{obErr}</p>}
          <button className="primary-button" style={{ width: "100%", justifyContent: "center", marginTop: 18 }} disabled={!agreeLater || agreeBusy}
            onClick={async () => {
              setAgreeBusy(true); setObErr(null);
              try { await api("agreement", { method: "POST", body: { agree: true } }); setData(await api("me")); }
              catch (e: any) { setObErr(e?.message || "Could not save that."); }
              finally { setAgreeBusy(false); }
            }}>
            {agreeBusy ? "Saving…" : "Accept & open my dashboard"}
          </button>
          <button className="ghost-button" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={logout}>Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="landing" style={{ minHeight: "100vh" }}>
      <header className="landing-nav">
        <div className="landing-shell landing-nav-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {a.logo_url ? (
              <span style={{ display: "grid", placeItems: "center", width: 36, height: 36, borderRadius: 9, background: "#fff", overflow: "hidden" }}>
                <img src={a.logo_url} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              </span>
            ) : (
              <span style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 9, background: "var(--ne-red-bright)", color: "#fff" }}>
                <BadgeCheck size={19} aria-hidden="true" />
              </span>
            )}
            <div style={{ lineHeight: 1.1 }}>
              <div style={{ fontWeight: 700 }}>{displayName}</div>
              <div className="muted small">Partner of {a.company_name}</div>
            </div>
          </div>
          <button className="ghost-button" onClick={logout}><LogOut size={14} aria-hidden="true" /> Sign out</button>
        </div>
      </header>

      <main className="landing-shell" style={{ paddingBottom: 70 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, margin: "30px 0 18px" }}>
          <div>
            <h1 className="landing-h2" style={{ textAlign: "left", margin: 0 }}>Hey {a.name.split(" ")[0]} 👋</h1>
            <p className="muted small" style={{ margin: "4px 0 0" }}>
              You earn <strong style={{ color: "var(--ne-text)" }}>{a.commission_pct}% of the success fee</strong> on every client you refer who gets funded.
            </p>
          </div>
        </div>

        {/* branded link */}
        <div className="landing-card" style={{ marginBottom: 18, borderLeft: "3px solid var(--ne-red-bright)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Link2 size={16} aria-hidden="true" style={{ color: "var(--ne-red-bright)" }} />
            <strong>Your referral link</strong>
          </div>
          <p className="muted small" style={{ margin: "0 0 10px" }}>
            Share this anywhere — text, email, social, your website. Anyone who signs up through it is credited to you automatically.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <code style={{ background: "var(--ne-black-2)", border: "1px solid var(--ne-line)", borderRadius: 8, padding: "11px 14px", wordBreak: "break-all", flex: "1 1 260px", fontSize: 14 }}>{a.link}</code>
            <button className="primary-button" onClick={copyLink}><Copy size={14} aria-hidden="true" /> {copied ? "Copied!" : "Copy link"}</button>
          </div>
        </div>

        {/* stats */}
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", marginBottom: 20 }}>
          <Stat icon={MousePointerClick} label="Link clicks" value={String(a.clicks)} />
          <Stat icon={Users} label="Clients referred" value={String(funnel.invited)} sub={`${funnel.applied} completed their application`} />
          <Stat icon={BadgeCheck} label="Clients approved" value={String(funnel.approved)} sub={t.approved_volume ? `${money(t.approved_volume)} in approvals` : "Approvals show here"} />
          <Stat icon={Wallet} label="Owed to you" value={money2(t.owed)} sub={`${money2(t.paid)} paid out so far`} accent />
        </div>

        {/* tabs */}
        <div className="profile-tabs" role="tablist" style={{ marginBottom: 14 }}>
          {([["clients", "My clients", Users], ["commissions", "Commissions", DollarSign], ["payouts", "Payouts", Banknote], ["account", "Account", KeyRound]] as const).map(([k, label, Icon]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "selected" : undefined} onClick={() => setTab(k as any)}>
              <Icon size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />{label}
            </button>
          ))}
        </div>

        {/* CLIENTS */}
        {tab === "clients" && (
          <div className="landing-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <strong>Everyone you've referred</strong>
              <span className="muted small">{funnel.invited} total · {funnel.applied} applied · {funnel.approved} approved</span>
            </div>
            {clients.length === 0 && (
              <p className="muted small">No clients yet — share your link and they'll appear here the moment they sign up.</p>
            )}
            {clients.map((c) => {
              const st = STAGE[c.status] || { label: c.status, tone: "" };
              return (
                <div key={c.id} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "12px 0", borderBottom: "1px solid var(--ne-line)" }}>
                  <span style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: "50%", background: "var(--ne-black-2)", border: "1px solid var(--ne-line)", fontWeight: 700, fontSize: 13, flex: "0 0 auto" }}>
                    {String(c.legal_name || "?").split(" ").map((x: string) => x[0]).slice(0, 2).join("").toUpperCase()}
                  </span>
                  <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                    <strong>{c.legal_name}</strong>
                    <div className="muted small">Referred {when(c.created_at)}{c.capital_requested ? ` · seeking ${money(c.capital_requested)}` : ""}</div>
                  </div>
                  <span className={`pill ${st.tone}`}>{st.label}</span>
                  <span className="muted small" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {c.intake_done ? <CheckCircle2 size={14} aria-hidden="true" style={{ color: "var(--ne-success-text)" }} /> : <Clock size={14} aria-hidden="true" />}
                    {c.intake_done ? "Application in" : "Application pending"}
                  </span>
                  {Number(c.approvals) > 0 ? (
                    <span className="pill pill-green" style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                      <TrendingUp size={13} aria-hidden="true" /> {money(c.approved_total)} approved
                    </span>
                  ) : (
                    <span className="muted small">No approvals yet</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* COMMISSIONS */}
        {tab === "commissions" && (
          <div className="landing-card">
            <strong style={{ display: "block", marginBottom: 10 }}>Your commission ledger</strong>
            {referrals.length === 0 && <p className="muted small">No commissions recorded yet — they post here as your clients' deals close.</p>}
            {referrals.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--ne-line)" }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <span>{r.description}</span>
                  {r.client_name && <div className="muted small">{r.client_name}</div>}
                </div>
                <span className="muted small">{money(r.sale_amount)} deal · {r.commission_pct}%</span>
                <strong>{money2(r.commission_amount)}</strong>
                <span className={`pill ${r.status === "paid" ? "pill-green" : r.status === "void" ? "pill-red" : "pill-amber"}`}>{r.status}</span>
                <span className="muted small">{when(r.paid_at || r.created_at)}</span>
              </div>
            ))}
          </div>
        )}

        {/* PAYOUTS */}
        {tab === "payouts" && (
          <div className="landing-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <strong>Payout statements</strong>
              <span className="muted small">Outstanding: <strong style={{ color: "var(--ne-text)" }}>{money2(t.owed)}</strong></span>
            </div>
            {payouts.length === 0 && <p className="muted small">No payouts yet. When {a.company_name} pays your commissions, each payout shows here with the amount, method, and reference.</p>}
            {payouts.map((p) => (
              <div key={p.id} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", padding: "12px 0", borderBottom: "1px solid var(--ne-line)" }}>
                <span style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 9, background: "var(--ne-black-2)", border: "1px solid var(--ne-line)", color: "var(--ne-success-text)" }}>
                  <Receipt size={16} aria-hidden="true" />
                </span>
                <div style={{ flex: "1 1 200px" }}>
                  <strong>{money2(p.amount)}</strong>
                  <div className="muted small">{p.items} commission{p.items === 1 ? "" : "s"}{p.period_start ? ` · ${when(p.period_start)} – ${when(p.period_end)}` : ""}</div>
                </div>
                <span className="muted small">{p.method ? p.method.toUpperCase() : "—"}{p.reference ? ` · ${p.reference}` : ""}</span>
                <span className="pill pill-green">Paid {when(p.paid_at)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ACCOUNT */}
        {tab === "account" && (
          <div style={{ display: "grid", gap: 16, maxWidth: 520 }}>
            <div className="landing-card">
              <strong style={{ display: "block", marginBottom: 4 }}>Your business logo</strong>
              <p className="muted small" style={{ marginBottom: 12 }}>{a.business_name || a.name} · alieffundinghub.com/with/{a.slug}</p>
              <LogoUploader logoUrl={a.logo_url} businessName={a.business_name || a.name} onChange={reload} compact />
            </div>
            <div className="landing-card">
              <strong style={{ display: "block", marginBottom: 4 }}>Partner agreement</strong>
              <p className="muted small" style={{ marginBottom: 10 }}>
                <FileCheck2 size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
                Accepted {when(a.agreement_accepted_at)} · {a.commission_pct}% of the success fee on each funded client you refer.
              </p>
              <details><summary className="muted small" style={{ cursor: "pointer" }}>Read the agreement</summary>
                <div style={{ marginTop: 10 }}><TermsBox company={a.company_name} pct={a.commission_pct} /></div>
              </details>
            </div>
          <div className="landing-card">
            <strong style={{ display: "block", marginBottom: 4 }}>Change your password</strong>
            <p className="muted small" style={{ marginBottom: 14 }}>Signed in as {a.email}</p>
            <label className="form-field" style={{ marginBottom: 10 }}><span>Current password</span>
              <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" /></label>
            <label className="form-field" style={{ marginBottom: 14 }}><span>New password (8+ characters)</span>
              <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" /></label>
            <button className="primary-button" disabled={pw.next.length < 8 || !pw.current} onClick={async () => {
              setPwMsg(null);
              try { await api("password", { method: "POST", body: pw }); setPwMsg("Password updated."); setPw({ current: "", next: "" }); }
              catch (e: any) { setPwMsg(e?.message || "Could not update password."); }
            }}>Update password</button>
            {pwMsg && <p className="muted small" style={{ marginTop: 10 }}>{pwMsg}</p>}
          </div>
          </div>
        )}
      </main>
    </div>
  );
}
